import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit, FiPlus, FiSave } from 'react-icons/fi'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import {
  useCreateSubscriptionPlan,
  useCreateSubscriptionPlanTier,
  useRefreshSubscriptionPlanCache,
  useSubscriptionPlans,
  useUpdateSubscriptionPlan,
  useUpdateSubscriptionPlanTier,
} from '@/hooks/useSystemConfigData'
import type {
  SystemSubscriptionPlan,
  SystemSubscriptionPlanTier,
} from '@/services/admin/systemService'
import {
  DEFAULT_MERCHANT_CONFIG_POLICY_JSON,
  formatMerchantConfigPolicyJsonForEditor,
  parseMerchantConfigPolicyJson,
} from '@/pages/systemConfig/subscriptionPlanPolicy'

type SubscriptionPlansTabProps = {
  cardBg: string
}

type PlanFormState = {
  name: string
  displayName: string
  description: string
  visibility: 'public' | 'internal'
  monthlyFeeUsd: string
  subscriptionPricingJson: string
  contractsDeployQuotaPerPeriod: string
  orderCreateQuotaPerPeriod: string
  maxLiveOrders: string
  maxMerchantCount: string
  maxApiKeysPerMerchant: string
  merchantConfigPolicyJson: string
  capabilitiesJson: string
  chainAccess: {
    evm: true
    tron: boolean
    solana: boolean
  }
  priorityLevel: 'low' | 'normal' | 'high'
  hasUsageTiers: boolean
  isActive: boolean
}

type TierFormState = {
  tierLevel: string
  minMonthlyVolumeUsd: string
  maxMonthlyVolumeUsd: string
  minSettledOrderCount: string
}

type TierEditingState = {
  plan: SystemSubscriptionPlan
  tier: SystemSubscriptionPlanTier | null
}

const emptyPlanForm: PlanFormState = {
  name: '',
  displayName: '',
  description: '',
  visibility: 'public',
  monthlyFeeUsd: '0',
  subscriptionPricingJson: '{"currency":"USD","unit":"month","pricesByQuantity":{"1":"0.00","3":"0.00","6":"0.00","12":"0.00"}}',
  contractsDeployQuotaPerPeriod: '0',
  orderCreateQuotaPerPeriod: '0',
  maxLiveOrders: '0',
  maxMerchantCount: '1',
  maxApiKeysPerMerchant: '2',
  merchantConfigPolicyJson: DEFAULT_MERCHANT_CONFIG_POLICY_JSON,
  capabilitiesJson: '{"plan_chain_access":{"enabledCompatibilities":["evm"]}}',
  chainAccess: {
    evm: true,
    tron: false,
    solana: false,
  },
  priorityLevel: 'normal',
  hasUsageTiers: true,
  // The API requires a new plan to be staged with its Tier 1 baseline before activation.
  isActive: false,
}

const emptyTierForm: TierFormState = {
  tierLevel: '',
  minMonthlyVolumeUsd: '0',
  maxMonthlyVolumeUsd: '',
  minSettledOrderCount: '0',
}

const toDateLabel = (value?: string): string => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const parseNonNegativeNumber = (value: string, field: string): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${field} must be a number >= 0`)
  }
  return parsed
}

const parseNonNegativeInteger = (value: string, field: string): number => {
  const parsed = parseNonNegativeNumber(value, field)
  if (!Number.isInteger(parsed)) {
    throw new Error(`${field} must be an integer`)
  }
  return parsed
}

const buildCapabilitiesJson = (chainAccess: PlanFormState['chainAccess']): string => {
  const enabledCompatibilities = ['evm']
  if (chainAccess.tron) enabledCompatibilities.push('tron')
  if (chainAccess.solana) enabledCompatibilities.push('solana')
  return JSON.stringify({
    plan_chain_access: {
      enabledCompatibilities,
    },
  })
}

const parseChainAccess = (
  capabilitiesJson: string
): PlanFormState['chainAccess'] => {
  try {
    const parsed = JSON.parse(capabilitiesJson) as {
      plan_chain_access?: { enabledCompatibilities?: unknown }
    }
    const enabled = parsed.plan_chain_access?.enabledCompatibilities
    if (!Array.isArray(enabled) || !enabled.includes('evm')) {
      return { evm: true, tron: false, solana: false }
    }
    return {
      evm: true,
      tron: enabled.includes('tron'),
      solana: enabled.includes('solana'),
    }
  } catch {
    return { evm: true, tron: false, solana: false }
  }
}

const formatJsonForDisplay = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

const planToForm = (plan: SystemSubscriptionPlan): PlanFormState => ({
  name: plan.name,
  displayName: plan.displayName,
  description: plan.description ?? '',
  visibility: plan.visibility,
  monthlyFeeUsd: String(plan.monthlyFeeUsd),
  subscriptionPricingJson: plan.subscriptionPricingJson ?? '{"currency":"USD","unit":"month","pricesByQuantity":{"1":"10.00","3":"28.00","6":"52.00","12":"100.00"}}',
  contractsDeployQuotaPerPeriod: String(plan.contractsDeployQuotaPerPeriod),
  orderCreateQuotaPerPeriod: String(plan.orderCreateQuotaPerPeriod),
  maxLiveOrders: String(plan.maxLiveOrders),
  maxMerchantCount: String(plan.maxMerchantCount),
  maxApiKeysPerMerchant: String(plan.maxApiKeysPerMerchant),
  merchantConfigPolicyJson: formatMerchantConfigPolicyJsonForEditor(
    plan.merchantConfigPolicyJson
  ),
  capabilitiesJson: formatJsonForDisplay(plan.capabilitiesJson),
  chainAccess: parseChainAccess(plan.capabilitiesJson),
  priorityLevel: plan.priorityLevel,
  hasUsageTiers: plan.hasUsageTiers,
  isActive: plan.isActive,
})

const tierToForm = (tier: SystemSubscriptionPlanTier): TierFormState => ({
  tierLevel: String(tier.tierLevel),
  minMonthlyVolumeUsd: String(tier.minMonthlyVolumeUsd),
  maxMonthlyVolumeUsd: tier.maxMonthlyVolumeUsd === null ? '' : String(tier.maxMonthlyVolumeUsd),
  minSettledOrderCount: String(tier.minSettledOrderCount),
})

export function SubscriptionPlansTab(props: SubscriptionPlansTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, isFetching, refetch } = useSubscriptionPlans()
  const refreshMutation = useRefreshSubscriptionPlanCache()
  const createPlanMutation = useCreateSubscriptionPlan()
  const updatePlanMutation = useUpdateSubscriptionPlan()
  const createTierMutation = useCreateSubscriptionPlanTier()
  const updateTierMutation = useUpdateSubscriptionPlanTier()
  const planModal = useDisclosure()
  const tierModal = useDisclosure()
  const [editingPlan, setEditingPlan] = useState<SystemSubscriptionPlan | null>(null)
  const [editingTier, setEditingTier] = useState<TierEditingState | null>(null)
  const [planForm, setPlanForm] = useState<PlanFormState>(emptyPlanForm)
  const [tierForm, setTierForm] = useState<TierFormState>(emptyTierForm)
  const [search, setSearch] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const priorityOptions = useMemo(
    () => [
      {
        value: 'low',
        label: t('systemConfig.subscriptionPlans.priority.low', { defaultValue: 'Low' }),
      },
      {
        value: 'normal',
        label: t('systemConfig.subscriptionPlans.priority.normal', { defaultValue: 'Normal' }),
      },
      {
        value: 'high',
        label: t('systemConfig.subscriptionPlans.priority.high', { defaultValue: 'High' }),
      },
    ],
    [t]
  )

  const visibilityOptions = useMemo(
    () => [
      {
        value: 'public',
        label: t('systemConfig.subscriptionPlans.visibility.public', { defaultValue: 'Public' }),
      },
      {
        value: 'internal',
        label: t('systemConfig.subscriptionPlans.visibility.internal', { defaultValue: 'Internal' }),
      },
    ],
    [t]
  )

  const plans = useMemo(() => {
    const rows = data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter(plan => (
      plan.id.toLowerCase().includes(needle)
      || plan.name.toLowerCase().includes(needle)
      || plan.displayName.toLowerCase().includes(needle)
      || (plan.description ?? '').toLowerCase().includes(needle)
    ))
  }, [data, search])

  const getPlanChainAccess = (plan: SystemSubscriptionPlan) =>
    parseChainAccess(plan.capabilitiesJson)

  const openCreatePlan = () => {
    setEditingPlan(null)
    setPlanForm(emptyPlanForm)
    setFormError(null)
    planModal.onOpen()
  }

  const openEditPlan = (plan: SystemSubscriptionPlan) => {
    setEditingPlan(plan)
    setPlanForm(planToForm(plan))
    setFormError(null)
    planModal.onOpen()
  }

  const openCreateTier = (plan: SystemSubscriptionPlan) => {
    setEditingTier({ plan, tier: null })
    setTierForm({
      ...emptyTierForm,
      tierLevel: String((plan.tiers.at(-1)?.tierLevel ?? 0) + 1),
    })
    setFormError(null)
    tierModal.onOpen()
  }

  const openEditTier = (plan: SystemSubscriptionPlan, tier: SystemSubscriptionPlanTier) => {
    setEditingTier({ plan, tier })
    setTierForm(tierToForm(tier))
    setFormError(null)
    tierModal.onOpen()
  }

  const buildPlanPayload = () => ({
    name: planForm.name.trim(),
    displayName: planForm.displayName.trim(),
    description: planForm.description.trim() || null,
    visibility: planForm.visibility,
    monthlyFeeUsd: parseNonNegativeNumber(planForm.monthlyFeeUsd, 'monthlyFeeUsd'),
    subscriptionPricingJson: planForm.subscriptionPricingJson,
    contractsDeployQuotaPerPeriod: parseNonNegativeInteger(
      planForm.contractsDeployQuotaPerPeriod,
      'contractsDeployQuotaPerPeriod'
    ),
    orderCreateQuotaPerPeriod: parseNonNegativeInteger(
      planForm.orderCreateQuotaPerPeriod,
      'orderCreateQuotaPerPeriod'
    ),
    maxLiveOrders: parseNonNegativeInteger(planForm.maxLiveOrders, 'maxLiveOrders'),
    maxMerchantCount: parseNonNegativeInteger(planForm.maxMerchantCount, 'maxMerchantCount'),
    maxApiKeysPerMerchant: parseNonNegativeInteger(
      planForm.maxApiKeysPerMerchant,
      'maxApiKeysPerMerchant'
    ),
    merchantConfigPolicyJson: parseMerchantConfigPolicyJson(
      planForm.merchantConfigPolicyJson
    ),
    capabilitiesJson: buildCapabilitiesJson(planForm.chainAccess),
    priorityLevel: planForm.priorityLevel,
    hasUsageTiers: planForm.hasUsageTiers,
    isActive: planForm.isActive,
  })

  const buildTierPayload = () => {
    const maxValue = tierForm.maxMonthlyVolumeUsd.trim()
    return {
      tierLevel: parseNonNegativeInteger(tierForm.tierLevel, 'tierLevel'),
      minMonthlyVolumeUsd: parseNonNegativeNumber(
        tierForm.minMonthlyVolumeUsd,
        'minMonthlyVolumeUsd'
      ),
      maxMonthlyVolumeUsd: maxValue
        ? parseNonNegativeNumber(maxValue, 'maxMonthlyVolumeUsd')
        : null,
      minSettledOrderCount: parseNonNegativeInteger(
        tierForm.minSettledOrderCount,
        'minSettledOrderCount'
      ),
    }
  }

  const handleSavePlan = async () => {
    try {
      setFormError(null)
      const payload = buildPlanPayload()
      if (editingPlan) {
        await updatePlanMutation.mutateAsync({ planId: editingPlan.id, updates: payload })
        toast.success(t('systemConfig.subscriptionPlans.messages.planUpdated', {
          defaultValue: 'Subscription plan updated',
        }))
      } else {
        await createPlanMutation.mutateAsync(payload)
        toast.success(t('systemConfig.subscriptionPlans.messages.planCreated', {
          defaultValue: 'Subscription plan created',
        }))
      }
      planModal.onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save subscription plan'
      setFormError(message)
      toast.error({ title: message })
    }
  }

  const handleSaveTier = async () => {
    if (!editingTier) return
    try {
      setFormError(null)
      const payload = buildTierPayload()
      if (payload.maxMonthlyVolumeUsd !== null
        && payload.maxMonthlyVolumeUsd < payload.minMonthlyVolumeUsd) {
        throw new Error('maxMonthlyVolumeUsd must be >= minMonthlyVolumeUsd')
      }
      if (editingTier.tier) {
        await updateTierMutation.mutateAsync({
          planId: editingTier.plan.id,
          tierId: editingTier.tier.id,
          updates: payload,
        })
        toast.success(t('systemConfig.subscriptionPlans.messages.tierUpdated', {
          defaultValue: 'Subscription tier updated',
        }))
      } else {
        await createTierMutation.mutateAsync({
          planId: editingTier.plan.id,
          tier: payload,
        })
        toast.success(t('systemConfig.subscriptionPlans.messages.tierCreated', {
          defaultValue: 'Subscription tier created',
        }))
      }
      tierModal.onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save subscription tier'
      setFormError(message)
      toast.error({ title: message })
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshMutation.mutateAsync()
      toast.success(t('systemConfig.actions.refreshCacheSuccess', {
        defaultValue: 'Cache refreshed',
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cache refresh failed'
      toast.error({ title: message })
    }
  }

  return (
    <VStack spacing={4} align="stretch">
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={3} wrap="wrap">
            <Box>
              <Heading size="md">
                {t('systemConfig.subscriptionPlans.title', {
                  defaultValue: 'Subscription Plans',
                })}
              </Heading>
              <Text color="gray.500" fontSize="sm" mt={1}>
                {t('systemConfig.subscriptionPlans.subtitle', {
                  defaultValue: 'Manage platform plan catalog and usage tiers.',
                })}
              </Text>
            </Box>
            <HStack spacing={2}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                isLoading={refreshMutation.isPending}
                onClick={handleRefreshCache}
              />
              <Button leftIcon={<FiPlus />} colorScheme="admin" size="sm" onClick={openCreatePlan}>
                {t('systemConfig.subscriptionPlans.actions.addPlan', {
                  defaultValue: 'Add Plan',
                })}
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <FormControl maxW={{ base: '100%', md: '360px' }}>
            <FormLabel fontSize="xs" mb={1}>
              {t('common.search', { defaultValue: 'Search' })}
            </FormLabel>
            <Input
              size="sm"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('systemConfig.subscriptionPlans.searchPlaceholder', {
                defaultValue: 'Search name, display name, or description',
              })}
            />
          </FormControl>
        </CardBody>
      </Card>

      <VStack align="stretch" spacing={4}>
        {plans.map(plan => (
          <Card key={plan.id} bg={props.cardBg}>
            <CardHeader>
              <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={3}>
                <VStack align="start" spacing={1}>
                  <HStack spacing={2} wrap="wrap">
                    <Heading size="sm">{plan.displayName}</Heading>
                    <Badge colorScheme={plan.isActive ? 'green' : 'gray'}>
                      {plan.isActive ? t('common.active') : t('common.inactive')}
                    </Badge>
                    <Badge colorScheme="blue">{plan.priorityLevel}</Badge>
                    <Badge colorScheme={plan.hasUsageTiers ? 'purple' : 'gray'}>
                      {plan.hasUsageTiers ? 'tiers' : 'flat'}
                    </Badge>
                    <Badge colorScheme={plan.visibility === 'internal' ? 'orange' : 'cyan'}>
                      {plan.visibility}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    {plan.name} · {plan.id}
                  </Text>
                  {plan.description && (
                    <Text fontSize="sm" color="gray.600">{plan.description}</Text>
                  )}
                </VStack>
                <HStack spacing={2}>
                  <IconButton
                    aria-label={t('systemConfig.subscriptionPlans.actions.editPlan', {
                      defaultValue: 'Edit Plan',
                    })}
                    icon={<FiEdit />}
                    size="sm"
                    onClick={() => openEditPlan(plan)}
                  />
                  <Button
                    leftIcon={<FiPlus />}
                    size="sm"
                    variant="outline"
                    onClick={() => openCreateTier(plan)}
                  >
                    {t('systemConfig.subscriptionPlans.actions.addTier', {
                      defaultValue: 'Add Tier',
                    })}
                  </Button>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{ base: 1, md: 5 }} spacing={3} mb={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500">Monthly fee</Text>
                  <Text fontWeight="semibold">${plan.monthlyFeeUsd}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Order quota</Text>
                  <Text fontWeight="semibold">{plan.orderCreateQuotaPerPeriod}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Contract quota</Text>
                  <Text fontWeight="semibold">{plan.contractsDeployQuotaPerPeriod}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Live orders</Text>
                  <Text fontWeight="semibold">{plan.maxLiveOrders}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Merchants / API keys</Text>
                  <Text fontWeight="semibold">
                    {plan.maxMerchantCount} / {plan.maxApiKeysPerMerchant}
                  </Text>
                </Box>
              </SimpleGrid>

              <Box mb={4}>
                <Text fontSize="xs" color="gray.500" mb={2}>Chain access</Text>
                <HStack spacing={2} wrap="wrap">
                  {(['evm', 'tron', 'solana'] as const).map(compatibility => {
                    const chainAccess = getPlanChainAccess(plan)
                    const enabled = chainAccess[compatibility]
                    return (
                      <Badge
                        key={compatibility}
                        colorScheme={enabled ? 'green' : 'gray'}
                        textTransform="uppercase"
                      >
                        {compatibility}
                      </Badge>
                    )
                  })}
                </HStack>
              </Box>

              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Tier</Th>
                    <Th>Monthly volume min</Th>
                    <Th>Monthly volume max</Th>
                    <Th>Settled orders min</Th>
                    <Th>Updated</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {plan.tiers.map(tier => (
                    <Tr key={tier.id}>
                      <Td>{tier.tierLevel}</Td>
                      <Td>${tier.minMonthlyVolumeUsd}</Td>
                      <Td>
                        {tier.maxMonthlyVolumeUsd === null ? 'Unlimited' : `$${tier.maxMonthlyVolumeUsd}`}
                      </Td>
                      <Td>{tier.minSettledOrderCount}</Td>
                      <Td>{toDateLabel(tier.updatedAt)}</Td>
                      <Td textAlign="right">
                        <IconButton
                          aria-label={t('systemConfig.subscriptionPlans.actions.editTier', {
                            defaultValue: 'Edit Tier',
                          })}
                          icon={<FiEdit />}
                          size="xs"
                          onClick={() => openEditTier(plan, tier)}
                        />
                      </Td>
                    </Tr>
                  ))}
                  {plan.tiers.length === 0 && (
                    <Tr>
                      <Td colSpan={6}>
                        <Text color="gray.500" fontSize="sm">
                          {t('systemConfig.subscriptionPlans.emptyTiers', {
                            defaultValue: 'No usage tiers configured.',
                          })}
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        ))}
        {plans.length === 0 && !isFetching && (
          <Card bg={props.cardBg}>
            <CardBody>
              <Text color="gray.500">
                {t('systemConfig.subscriptionPlans.emptyPlans', {
                  defaultValue: 'No subscription plans found.',
                })}
              </Text>
            </CardBody>
          </Card>
        )}
      </VStack>

      <Modal isOpen={planModal.isOpen} onClose={planModal.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>
            {editingPlan
              ? t('systemConfig.subscriptionPlans.editPlanTitle', {
                defaultValue: 'Edit Subscription Plan',
              })
              : t('systemConfig.subscriptionPlans.createPlanTitle', {
                defaultValue: 'Create Subscription Plan',
              })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {formError && <Text color="red.500">{formError}</Text>}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={planForm.name}
                    onChange={event => setPlanForm({ ...planForm, name: event.target.value })}
                  />
                  <FormHelperText>Lowercase catalog code, for example free or pro.</FormHelperText>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Display name</FormLabel>
                  <Input
                    value={planForm.displayName}
                    onChange={event => setPlanForm({
                      ...planForm,
                      displayName: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Visibility</FormLabel>
                  <DropdownSelect
                    value={planForm.visibility}
                    options={visibilityOptions}
                    onChange={value => setPlanForm({
                      ...planForm,
                      visibility: value as PlanFormState['visibility'],
                    })}
                  />
                  <FormHelperText>
                    Public plans are eligible for merchant-facing self-service surfaces; internal plans are admin-only.
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Monthly fee USD</FormLabel>
                  <Input
                    value={planForm.monthlyFeeUsd}
                    onChange={event => setPlanForm({
                      ...planForm,
                      monthlyFeeUsd: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Prepaid prices by quantity</FormLabel>
                  <Textarea
                    value={planForm.subscriptionPricingJson}
                    onChange={event => setPlanForm({
                      ...planForm,
                      subscriptionPricingJson: event.target.value,
                    })}
                    fontFamily="mono"
                    rows={4}
                  />
                  <FormHelperText>USD totals for quantities 1, 3, 6, and 12.</FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Priority</FormLabel>
                  <DropdownSelect
                    value={planForm.priorityLevel}
                    options={priorityOptions}
                    onChange={value => setPlanForm({
                      ...planForm,
                      priorityLevel: value as PlanFormState['priorityLevel'],
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Order quota per period</FormLabel>
                  <Input
                    value={planForm.orderCreateQuotaPerPeriod}
                    onChange={event => setPlanForm({
                      ...planForm,
                      orderCreateQuotaPerPeriod: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Contract deploy quota per period</FormLabel>
                  <Input
                    value={planForm.contractsDeployQuotaPerPeriod}
                    onChange={event => setPlanForm({
                      ...planForm,
                      contractsDeployQuotaPerPeriod: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Max live orders</FormLabel>
                  <Input
                    value={planForm.maxLiveOrders}
                    onChange={event => setPlanForm({
                      ...planForm,
                      maxLiveOrders: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Max merchant count</FormLabel>
                  <Input
                    value={planForm.maxMerchantCount}
                    onChange={event => setPlanForm({
                      ...planForm,
                      maxMerchantCount: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Max API keys per merchant</FormLabel>
                  <Input
                    value={planForm.maxApiKeysPerMerchant}
                    onChange={event => setPlanForm({
                      ...planForm,
                      maxApiKeysPerMerchant: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input
                    value={planForm.description}
                    onChange={event => setPlanForm({
                      ...planForm,
                      description: event.target.value,
                    })}
                  />
                </FormControl>
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Active</FormLabel>
                  <Switch
                    isChecked={planForm.isActive}
                    isDisabled={!editingPlan}
                    onChange={event => setPlanForm({
                      ...planForm,
                      isActive: event.target.checked,
                    })}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Has usage tiers</FormLabel>
                  <Switch
                    isChecked={planForm.hasUsageTiers}
                    onChange={event => setPlanForm({
                      ...planForm,
                      hasUsageTiers: event.target.checked,
                    })}
                  />
                </FormControl>
              </SimpleGrid>
              <Box borderWidth="1px" borderRadius="lg" p={4}>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Heading size="sm">Chain Access</Heading>
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      Controls which chain compatibilities this plan can enable for new order
                      capability.
                    </Text>
                  </Box>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">EVM</FormLabel>
                      <Switch isChecked isDisabled />
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">TRON</FormLabel>
                      <Switch
                        isChecked={planForm.chainAccess.tron}
                        onChange={event => {
                          const chainAccess = {
                            ...planForm.chainAccess,
                            tron: event.target.checked,
                          }
                          setPlanForm({
                            ...planForm,
                            chainAccess,
                            capabilitiesJson: formatJsonForDisplay(
                              buildCapabilitiesJson(chainAccess)
                            ),
                          })
                        }}
                      />
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">Solana</FormLabel>
                      <Switch
                        isChecked={planForm.chainAccess.solana}
                        onChange={event => {
                          const chainAccess = {
                            ...planForm.chainAccess,
                            solana: event.target.checked,
                          }
                          setPlanForm({
                            ...planForm,
                            chainAccess,
                            capabilitiesJson: formatJsonForDisplay(
                              buildCapabilitiesJson(chainAccess)
                            ),
                          })
                        }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel>Capabilities JSON (read only)</FormLabel>
                    <Textarea
                      minH="96px"
                      fontFamily="mono"
                      value={planForm.capabilitiesJson}
                      isReadOnly
                    />
                    <FormHelperText>
                      Saved payload is generated from the switches above; invalid historical JSON
                      is displayed here for diagnosis and treated as EVM-only until saved.
                    </FormHelperText>
                  </FormControl>
                </VStack>
              </Box>
              <FormControl>
                <FormLabel>Merchant config policy JSON</FormLabel>
                <Textarea
                  minH="180px"
                  fontFamily="mono"
                  value={planForm.merchantConfigPolicyJson}
                  onChange={event => setPlanForm({
                    ...planForm,
                    merchantConfigPolicyJson: event.target.value,
                  })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={planModal.onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="admin"
              isLoading={createPlanMutation.isPending || updatePlanMutation.isPending}
              onClick={handleSavePlan}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={tierModal.isOpen} onClose={tierModal.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>
            {editingTier?.tier
              ? t('systemConfig.subscriptionPlans.editTierTitle', {
                defaultValue: 'Edit Usage Tier',
              })
              : t('systemConfig.subscriptionPlans.createTierTitle', {
                defaultValue: 'Create Usage Tier',
              })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {formError && <Text color="red.500">{formError}</Text>}
              <Text fontSize="sm" color="gray.500">
                {editingTier?.plan.displayName} · {editingTier?.plan.name}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Tier level</FormLabel>
                  <Input
                    value={tierForm.tierLevel}
                    onChange={event => setTierForm({
                      ...tierForm,
                      tierLevel: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Min monthly volume USD</FormLabel>
                  <Input
                    value={tierForm.minMonthlyVolumeUsd}
                    onChange={event => setTierForm({
                      ...tierForm,
                      minMonthlyVolumeUsd: event.target.value,
                    })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Max monthly volume USD</FormLabel>
                  <Input
                    value={tierForm.maxMonthlyVolumeUsd}
                    onChange={event => setTierForm({
                      ...tierForm,
                      maxMonthlyVolumeUsd: event.target.value,
                    })}
                  />
                  <FormHelperText>Leave blank for unlimited upper bound.</FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Min settled order count</FormLabel>
                  <Input
                    value={tierForm.minSettledOrderCount}
                    onChange={event => setTierForm({
                      ...tierForm,
                      minSettledOrderCount: event.target.value,
                    })}
                  />
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={tierModal.onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="admin"
              isLoading={createTierMutation.isPending || updateTierMutation.isPending}
              onClick={handleSaveTier}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
